import { create } from 'zustand'
import type { Estimate, LineItem, EstimateNote } from '@/types'

interface EstimateState {
  currentEstimate: Estimate | null
  lineItems: LineItem[]
  notes: EstimateNote[]
  isLoading: boolean
  isSaving: boolean
  activeCategory: string

  setEstimate: (estimate: Estimate) => void
  updateEstimateFields: (fields: Partial<Estimate>) => void
  setLineItems: (items: LineItem[]) => void
  addLineItem: (item: LineItem) => void
  updateLineItem: (id: string, data: Partial<LineItem>) => void
  removeLineItem: (id: string) => void
  setNotes: (notes: EstimateNote[]) => void
  addNote: (note: EstimateNote) => void
  setMarkup: (markup: number) => void
  setActiveCategory: (category: string) => void
  setLoading: (loading: boolean) => void
  setSaving: (saving: boolean) => void
  reset: () => void
}

export const useEstimateStore = create<EstimateState>((set, get) => ({
  currentEstimate: null,
  lineItems: [],
  notes: [],
  isLoading: false,
  isSaving: false,
  activeCategory: 'all',

  setEstimate: (estimate) => {
    set({
      currentEstimate: estimate,
      lineItems: estimate.lineItems || [],
      notes: estimate.notes || [],
    })
  },

  // Update specific fields on currentEstimate WITHOUT touching lineItems or notes
  updateEstimateFields: (fields) => {
    const { currentEstimate } = get()
    if (!currentEstimate) return
    set({ currentEstimate: { ...currentEstimate, ...fields } })
  },

  setLineItems: (items) => set({ lineItems: items }),

  addLineItem: (item) => {
    const { lineItems, currentEstimate } = get()
    const updated = [...lineItems, item]
    const subtotal = updated.reduce((sum, li) => sum + (li.lineTotal || 0), 0)
    const markup = currentEstimate?.defaultMarkup ?? 0.15
    set({
      lineItems: updated,
      currentEstimate: currentEstimate ? {
        ...currentEstimate,
        subtotal,
        markupAmount: subtotal * markup,
        total: subtotal + subtotal * markup,
      } : null,
    })
  },

  updateLineItem: (id, data) => {
    const { lineItems, currentEstimate } = get()
    const updated = lineItems.map((li) =>
      li.id === id ? { ...li, ...data } : li
    )
    const subtotal = updated.reduce((sum, li) => sum + (li.lineTotal || 0), 0)
    const markup = currentEstimate?.defaultMarkup ?? 0.15
    set({
      lineItems: updated,
      currentEstimate: currentEstimate ? {
        ...currentEstimate,
        subtotal,
        markupAmount: subtotal * markup,
        total: subtotal + subtotal * markup,
      } : null,
    })
  },

  removeLineItem: (id) => {
    const { lineItems, currentEstimate } = get()
    const updated = lineItems.filter((li) => li.id !== id)
    const subtotal = updated.reduce((sum, li) => sum + (li.lineTotal || 0), 0)
    const markup = currentEstimate?.defaultMarkup ?? 0.15
    set({
      lineItems: updated,
      currentEstimate: currentEstimate ? {
        ...currentEstimate,
        subtotal,
        markupAmount: subtotal * markup,
        total: subtotal + subtotal * markup,
      } : null,
    })
  },

  setNotes: (notes) => set({ notes }),

  addNote: (note) => {
    const { notes } = get()
    set({ notes: [note, ...notes] })
  },

  setMarkup: (markup) => {
    const { currentEstimate } = get()
    if (!currentEstimate) return
    const subtotal = currentEstimate.subtotal
    set({
      currentEstimate: {
        ...currentEstimate,
        defaultMarkup: markup,
        markupAmount: subtotal * markup,
        total: subtotal + subtotal * markup,
      },
    })
  },

  setActiveCategory: (category) => set({ activeCategory: category }),

  setLoading: (isLoading) => set({ isLoading }),

  setSaving: (isSaving) => set({ isSaving }),

  reset: () => set({
    currentEstimate: null,
    lineItems: [],
    notes: [],
    isLoading: false,
    isSaving: false,
    activeCategory: 'all',
  }),
}))
